// homepage-pin.ts — FOUNDER-WALK-LIVE-RECONCILE-025-R3: "use the same pins and style from the home page map".
//
// Every token below is COPIED from the design export's homepage map (public/design/sg-beta-v2.01.html:
// L_PIN_D, L_SELB, L_SELC, L_TIERS, ORANGE, `_lRender`'s land fill/stroke and label attributes). The R3
// unit test extracts the same strings from the export and asserts byte equality, so this is a shared
// extraction, not an approximation. Used by KeylessMap on the `mountain-select` surface only.

import type { PinStatus } from "./pin-markers";

export const HOME_PIN_D = "M0,0 C-2.6,-3.6 -13,-8.2 -13,-17 A13,13 0 1,1 13,-17 C13,-8.2 2.6,-3.6 0,0 Z";
export const HOME_SELB = "M26.7198 3.29803C28.4902 0.184513 32.9161 0.184512 34.6864 3.29803L60.08 47.9575C61.8504 51.071 59.6375 54.9629 56.0967 54.9629H5.30952C1.7688 54.9629 -0.444153 51.071 1.32621 47.9575L26.7198 3.29803Z";
export const HOME_SELC = "M28.9963 9.66706C29.7698 8.37203 31.6438 8.36667 32.4246 9.65725L46.279 32.5566C46.8867 33.561 46.5026 34.8705 45.4485 35.3875L39.8893 38.1143C39.1751 38.4646 38.3212 38.3584 37.7146 37.8438L29.3914 30.7823C28.7231 30.2153 27.7634 30.1495 27.0239 30.62L22.7768 33.3224C22.2838 33.6362 21.6781 33.7186 21.1192 33.5479L18.1613 32.6449C16.9192 32.2656 16.3623 30.8216 17.0282 29.7065L28.9963 9.66706Z";
/** the glyph group's transform inside the pin body — `mg` in `_lRender` */
export const HOME_GLYPH_TRANSFORM = "translate(-8.3,-25.9) scale(0.272)";
export const HOME_TIERS = [
  { tier: 2, label: "Book now", color: "#3FA80B" },
  { tier: 1, label: "Info only", color: "#E2900E" },
  { tier: 0, label: "Not yet", color: "#9AA7AE" },
] as const;
export const HOME_ORANGE = "#FC500E";
export const HOME_LAND = "#DEE9EC";
export const HOME_LAND_STROKE = "#fff";
export const HOME_OCEAN = "#fff";
export const HOME_LABEL = { fill: "#33454E", halo: "#fff", haloWidth: 2.6, font: "DIN Pro, Roboto, sans-serif", size: 10, weight: 700, letterSpacing: "0.06em" } as const;
/** `_lRender`: the hovered pin scales 1.22 and its stroke/glyph go `d3.color(c).brighter(0.35)` */
export const HOME_HOVER_SCALE = 1.22;
export const HOME_HOVER_BRIGHTER = 0.35;

/** The homepage's own status → tier rule: bookable = "Book now", info-only = "Info only", unknown falls to
 *  L_TIERS[2] ("Not yet"), exactly as `tierOf` does for a pin with no matching tier. */
export function homepageTierColour(status?: PinStatus): string {
  if (status === "bookable") return HOME_TIERS[0].color;
  if (status === "info-only") return HOME_TIERS[1].color;
  return HOME_TIERS[2].color;
}

/** d3-color's `brighter(k)`: each channel × (1/0.7)^k, clamped — the same arithmetic the homepage runs. */
export function brighter(hex: string, k = HOME_HOVER_BRIGHTER): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const f = Math.pow(1 / 0.7, k);
  const ch = (h: string) => Math.max(0, Math.min(255, Math.round(parseInt(h, 16) * f))).toString(16).padStart(2, "0");
  return `#${ch(m[1]!)}${ch(m[2]!)}${ch(m[3]!)}`;
}

/**
 * The homepage pin as inline SVG: white teardrop body stroked in the tier colour, the mountain glyph in
 * the tier colour with the white peak on top. `hover` applies the homepage's brighter stroke/glyph; the
 * 1.22 scale is applied by the caller on the pin element (maplibre owns the marker root's transform).
 * Native size (26 × 30 at scale 1); the body path spans x −13..13, y −30..0.
 */
export function homepagePinSvg(status?: PinStatus, opts: { hover?: boolean; width?: number } = {}): string {
  const base = homepageTierColour(status);
  const c = opts.hover ? brighter(base) : base;
  const w = opts.width ?? 26;
  const h = Math.round((w * 32) / 28);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="-14 -31 28 32" data-pin-kind="mountain" data-pin-source="homepage" data-pin-tier="${c}" aria-hidden="true">` +
    `<path d="${HOME_PIN_D}" fill="#fff" stroke="${c}" stroke-width="1.5" stroke-linejoin="round"/>` +
    `<g transform="${HOME_GLYPH_TRANSFORM}"><path d="${HOME_SELB}" fill="${c}"/><path d="${HOME_SELC}" fill="#fff"/></g>` +
    `</svg>`
  );
}

// ── THE PALE MAP — the homepage's land/ocean/label treatment applied to the vendor vector style ─────
// The homepage draws land #DEE9EC with a white stroke over a white ground and shows only restrained
// place labels. On the selector's vector map (OpenMapTiles schema via OpenFreeMap) the same reading is:
// background = land (#DEE9EC), water polygons = ocean/lakes (white), every other fill flattened away,
// roads/rail/buildings/POIs hidden, boundaries drawn white, and only `place` labels kept in the homepage
// label colour with a white halo. Keyed on `source-layer`/`type`, never on a vendor's layer ids.
type Layer = { id?: string; type?: string; "source-layer"?: string; paint?: Record<string, unknown>; layout?: Record<string, unknown>; [k: string]: unknown };
type StyleLike = { layers?: Layer[]; [k: string]: unknown };

const HIDE_SOURCE_LAYERS = new Set(["landcover", "landuse", "park", "building", "transportation", "transportation_name", "aeroway", "aerodrome_label", "poi", "housenumber", "mountain_peak", "water_name", "waterway"]);

export function homepagePalette<T extends StyleLike>(style: T): T {
  if (!style || !Array.isArray(style.layers)) return style;
  const layers = style.layers.map((l): Layer => {
    if (!l || typeof l !== "object") return l;
    const sl = l["source-layer"];
    if (l.type === "background") return { ...l, paint: { ...(l.paint ?? {}), "background-color": HOME_LAND } };
    if (sl && HIDE_SOURCE_LAYERS.has(sl)) return { ...l, layout: { ...(l.layout ?? {}), visibility: "none" } };
    if (sl === "water" && l.type === "fill") return { ...l, paint: { ...(l.paint ?? {}), "fill-color": HOME_OCEAN, "fill-opacity": 1 } };
    if (sl === "boundary" && l.type === "line") return { ...l, paint: { ...(l.paint ?? {}), "line-color": HOME_LAND_STROKE, "line-opacity": 1 } };
    if (sl === "place" && l.type === "symbol") {
      return { ...l, paint: { ...(l.paint ?? {}), "text-color": HOME_LABEL.fill, "text-halo-color": HOME_LABEL.halo, "text-halo-width": 1.3 } };
    }
    return l;
  });
  return { ...style, layers };
}
